import { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./ReportSearchBar.module.scss";
import { FiChevronDown as DropdownIcon } from "react-icons/fi";
import { AiOutlineSearch as SearchIcon } from "react-icons/ai";
import { components } from "react-select";
import AsyncSelect from "react-select/async";
import {
  DashboardDataPieces,
  Report,
  ReportDetailsData,
  UserRole,
} from "../../../../../interfaces/interfaces";
import { RestS } from "../../../../../rest/rest-service";
// import { ReportDetailstContext } from "../../../context/ReportContext";
import { debounce, initial, isEmpty } from "lodash";
import { useLocation, useNavigate } from "react-router";
// import { sampleCompanies } from "./sampleData";
import classNames from "classnames";
import { useAppDispatch } from "../../../../../redux/reduxHooks";
import { deleteTempWorkspace } from "../../../../../redux/temporaryWorkspase/temporaryWorkspace";
import { setSelectedCompanyForAgency } from "../../../../../redux/decisionHub/decisionHub";
import { getLocalUser } from "../../../../../components/functions/utilities";

const Menu = (props) => {
  return <div className={styles.menu} {...props} />;
};

const SearchBlanket = (props) => (
  <div
    className={styles.searchBlanket}
    css={{
      bottom: 0,
      left: 0,
      top: 0,
      right: 0,
      position: "fixed",
      zIndex: 1,
    }}
    {...props}
  />
);

export const Dropdown = ({ children, isOpen, target, onClose }) => (
  <div className={styles.dropdown}>
    {target}
    {isOpen ? <Menu>{children}</Menu> : null}
    {isOpen ? <SearchBlanket onClick={onClose} /> : null}
  </div>
);

const selectStyles = {
  control: (provided) => ({
    ...provided,
    minWidth: 240,
    margin: 8,
    border: 0,
    // This line disable the blue border
    boxShadow: "none",
    backgroundColor: "#E7EBF3",
  }),
  menu: () => ({ boxShadow: "inset 0 1px 0 rgba(0, 0, 0, 0.1)" }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    // const color = chroma(data.color);
    return {
      ...styles,
      cursor: "pointer",
      backgroundColor: "transparent",
      color: isSelected ? "#965ae5" : "#808080",
      borderTop: "1px solid #d5d5d5",
      padding: "1rem",
      ":first-of-type": {
        borderTop: "1px solid #fff",
      },
      ":active": {
        ...styles[":active"],
        backgroundColor: "transparent",
      },
    };
  },
};

const SearchIconIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <SearchIcon />
    </components.DropdownIndicator>
  );
};

// const options = [
//   { value: "1", label: "Item 1" },
//   { value: "2", label: "Item 2" },
//   { value: "3", label: "Item 3" },
//   { value: "4", label: "Item 4" },
//   { value: "5", label: "Item 5" },
//   { value: "6", label: "Item 6" },
//   { value: "7", label: "Item 7" },
// ];

export enum SearchBarType {
  dashboard = "dashboard",
  decisionHub = "decisionHub",
  workspace = "workspace",
  financeForm = "financeForm"
}

type Props = {
  type: SearchBarType;
  data: ReportDetailsData | DashboardDataPieces | any;
  setSelected?: (arg: any) => void;
};

interface HelperTemplate {
  getDefaultSelectedValue: (arg: any) => any;
  loadOptions: (arg: any) => any;
  onSelectChange: (arg: any) => any;
  displaySelectedValue: (arg: any) => any;
  getOptionsDebounced: (arg: any) => any;
  getOptionLabel: (arg: any) => any;
  getOptionValue: (arg: any) => any;
  placeholder: string;
}

const ReportSearchBar = ({ data, type, setSelected }: Props) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [initialOptions, setInitialOptions] = useState([]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const user = getLocalUser();

  const helperTemplate: Record<string, HelperTemplate> = useMemo(() => {
    return {
      workspace: {
        getDefaultSelectedValue: (data) => {
          dispatch(
            setSelectedCompanyForAgency({
              id: data?.createdUser?.companyId,
              name: data?.createdUser?.companyName,
            }),
          );
          return data?.report;
        },
        loadOptions: async ({
          data,
          inputValue,
          callback,
          isInitial = false,
        }) => {
          const filterCompanies = (inputValue: string, options: Report[]) => {
            return options.filter((i) =>
              i.companyName.toLowerCase().includes(inputValue.toLowerCase()),
            );
          };

          try {
            const reports = await RestS.getAllCompaniesReportByReportName(
              data.report?.initialReportName,
              inputValue ? inputValue : null,
            );

            if (isInitial) return reports;
            callback(null, { options: filterCompanies(inputValue, reports) });
          } catch (error) {
            console.log(error);
          }
        },
        onSelectChange: ({ value, selectedValue, navigate, dispatch }) => {
          if (value?.id !== selectedValue.id) {
            navigate(`/ReportDetails/${value.id}`, { replace: true });
            dispatch(
              setSelectedCompanyForAgency({
                id: value.id,
                name: value.companyName,
              }),
            );
            dispatch(deleteTempWorkspace({}));
          }
        },
        displaySelectedValue: (selectedValue) => {
          return selectedValue
            ? `${selectedValue.companyName}`
            : "Select A Value";
        },
        getOptionsDebounced: ({ data, inputValue, callback }) => {
          return debounce((inputValue, callback) => {
            if (!inputValue || inputValue.length == 0)
              return Promise.resolve([]);

            return RestS.getAllCompaniesReportByReportName(
              data.report?.initialReportName,
              inputValue,
            )
              .then((value) => value)
              .then((result) => {
                callback(result);
              })
              .catch((error) => callback(error));
          }, 300)(inputValue, callback);
        },
        getOptionLabel: (report: Report) => report?.companyName,
        getOptionValue: (report: Report) => report.id.toString(),
        placeholder: "Search Clients",
      },
      dashboard: {
        getDefaultSelectedValue: (options) => {
          return options.length > 0
            ? options[0]
            : { label: "All Clients", value: "All Clients" };
        },
        loadOptions: async ({ data }) => {
          const idMap = {};
          const options = new Set();
          Object.values(data).map((items: Array<any>) => {
            items.map((item) => {
              options.add(item.companyName);
              idMap[item.companyName] = item.id;
            });
          });

          const optionsArray = [
            { label: "All Clients", value: "All Clients" },
            ...Array.from(options).map((option: string) => ({
              id: idMap[option],
              value: option,
              label: option,
            })),
          ];

          console.log('optionsArray', optionsArray);
          // i.e. with all acounts and one more option
          if (optionsArray.length === 2) return optionsArray.slice(-1);
          return optionsArray;
        },
        onSelectChange: ({ value, setSelectedValue, navigate }) => {
          setSelectedValue(value);
          if (value.value !== "All Clients")
            navigate(`?client=${encodeURIComponent(value?.value)}`);
          else navigate("/");
        },
        displaySelectedValue: (selectedValue) => {
          return selectedValue?.value;
        },
        getOptionsDebounced: ({ initialOptions, inputValue, callback }) => {
          return debounce((inputValue, callback) => {
            if (!inputValue || inputValue.length == 0)
              return Promise.resolve([]);

            const result = initialOptions
              .slice(1)
              .filter((item: any) =>
                item.label.toLowerCase().includes(inputValue.toLowerCase()),
              );
            callback(result);
          }, 300)(inputValue, callback);
        },
        getOptionLabel: (option) => option.label,
        getOptionValue: (option) => option.value,
        placeholder: "Search",
      },
      financeForm: {
        getDefaultSelectedValue: (options) => {
          return { label: "Select Metric", value: "Select Metric" }
        },
        loadOptions: async ({ data }) => {
          const optionsArray = data.map((option) => {
            return {
              value: option?.value,
              label: option?.label
            }
          })
          return optionsArray;
        },
        onSelectChange: ({ value, setSelectedValue, navigate }) => {
          console.log('value', value, selectedValue)
          // if (value && value.value !== "All Clients") {
          //   setSelectedValue(value);
          //   dispatch(
          //     setSelectedCompanyForAgency({
          //       id: value.value,
          //       name: value.label,
          //     }),
          //   );
          //   navigate(`?client=${value.label}`);
          // }
        },
        displaySelectedValue: (selectedValue) => {
          return selectedValue?.label;
        },
        getOptionsDebounced: ({ initialOptions, inputValue, callback }) => {
          return debounce((inputValue, callback) => {
            if (!inputValue || inputValue.length == 0)
              return Promise.resolve([]);

            const result = initialOptions
              .slice(1)
              .filter((item: any) =>
                item.label.toLowerCase().includes(inputValue.toLowerCase()),
              );
            callback(result);
          }, 300)(inputValue, callback);
        },
        getOptionLabel: (option) => option.label,
        getOptionValue: (option) => option.value,
        placeholder: "Search",
      },
      decisionHub: {
        getDefaultSelectedValue: (options) => {
          if (options.length > 0) return options[0];
          return null;
        },
        loadOptions: async ({ data }) => {
          const options = [];
          Object.values(data).map((item: Record<string, string>) => {
            options.push({ name: item.name, id: item.id });
          });

          const optionsArray = Array.from(options).map((option) => ({
            value: option.id,
            label: option.name,
          }));
          // i.e. with all acounts and one more option
          // if (optionsArray.length === 2) return optionsArray.slice(-1);
          return optionsArray;
        },
        onSelectChange: ({ value, setSelectedValue, navigate }) => {
          if (value && value.value !== "All Clients") {
            setSelectedValue(value);
            dispatch(
              setSelectedCompanyForAgency({
                id: value.value,
                name: value.label,
              }),
            );
            navigate(`?client=${value.label}`);
          }
        },
        displaySelectedValue: (selectedValue) => {
          return selectedValue?.label;
        },
        getOptionsDebounced: ({ initialOptions, inputValue, callback }) => {
          return debounce((inputValue, callback) => {
            if (!inputValue || inputValue.length == 0)
              return Promise.resolve([]);
            const result = initialOptions
              .slice(1)
              .filter((item: any) =>
                item.label.toLowerCase().includes(inputValue.toLowerCase()),
              );
            callback(result);
          }, 300)(inputValue, callback);
        },
        getOptionLabel: (option) => option.label,
        getOptionValue: (option) => option.value,
        placeholder: "Search",
      },
    };
  }, [pathname]);

  const arrayHash = useCallback((a) => {
    const charsum = function (s: string) {
      let sum = 0;
      for (let i = 0; i < s.length; i++) {
        sum += s.charCodeAt(i) * i;
      }
      return sum;
    };

    let sum = 0;
    a.forEach((item: any) => {
      let cs = charsum(item);
      sum += 65027 / cs;
    });

    return ("" + sum).slice(0, 16);
  }, []);

  const config = useMemo(() => {
    const helper: HelperTemplate = helperTemplate[type];

    if (type === SearchBarType.workspace && user?.role === UserRole.Agency)
      dispatch(
        setSelectedCompanyForAgency({
          id: data.createdUser?.companyId,
        }),
      );

    return {
      useEffectDeps:
        type === SearchBarType.dashboard
          ? [data]
          : type === SearchBarType.decisionHub
            ? [arrayHash(data)]
            : [],
      initialOptionsGenerator:
        type === SearchBarType.workspace
          ? async () =>
            await helper.loadOptions({
              data: data,
              inputValue: null,
              callback: null,
              isInitial: true,
            })
          : () => helper.loadOptions({ data }),
      ...helper,
    };
  }, [data, pathname]);

  const [selectedValue, setSelectedValue] = useState(
    type === SearchBarType.dashboard || type === SearchBarType.decisionHub
      ? config.getDefaultSelectedValue(initialOptions)
      : config.getDefaultSelectedValue(data),
  );

  useEffect(() => {
    if (type === SearchBarType.dashboard) {
      config.onSelectChange({
        value: config.getDefaultSelectedValue(initialOptions),
        setSelectedValue,
        navigate,
      });
    } else if (type === SearchBarType.decisionHub) {
      config.onSelectChange({
        value: config.getDefaultSelectedValue(initialOptions),
        setSelectedValue,
        navigate,
      });
    }
  }, [initialOptions]);

  useEffect(() => {
    (async () => {
      const options = await config.initialOptionsGenerator();
      setInitialOptions(options);
    })();
  }, config.useEffectDeps);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const onSelectChange = (value: any) => {
    setIsDropdownOpen(!isDropdownOpen);

    if ([SearchBarType.dashboard, SearchBarType.decisionHub].includes(type))
      config.onSelectChange({
        value,
        setSelectedValue,
        navigate,
      });
    else config.onSelectChange({ value, selectedValue, navigate, dispatch });
  };

  return (
    <div
      className={classNames(
        styles.dropdownContainer,
        type === SearchBarType.dashboard && styles.dashboard,
        type == SearchBarType.workspace && styles.workspace,
        initialOptions.length === 0 && styles.displayNone,
      )}
    >
      <Dropdown
        isOpen={isDropdownOpen}
        onClose={toggleDropdown}
        target={
          <button className={styles.selectButton} onClick={toggleDropdown}>
            <div className={styles.btnContainer}>
              <div className={styles.btnText}>
                {config.displaySelectedValue(selectedValue)}
              </div>
              <div className={styles.iconContainer}>
                <DropdownIcon />
              </div>
            </div>
          </button>
        }
      >
        {/* <AsyncSelect<Report> */}
        <AsyncSelect
          autoFocus
          //   cacheOptions={true}
          backspaceRemovesValue={true}
          components={{
            DropdownIndicator: SearchIconIndicator,
            IndicatorSeparator: null,
          }}
          controlShouldRenderValue={false}
          hideSelectedOptions={false}
          isClearable={false}
          menuIsOpen
          onChange={onSelectChange}
          loadOptions={(inputValue, callback) =>
            config.getOptionsDebounced({
              data,
              initialOptions,
              inputValue,
              callback,
            })
          }
          defaultOptions={initialOptions}
          placeholder={config.placeholder}
          styles={selectStyles}
          getOptionLabel={config.getOptionLabel}
          getOptionValue={config.getOptionValue}
          value={selectedValue}
        />
      </Dropdown>
    </div>
  );
};

export default ReportSearchBar;
